import { makeStyles, tokens } from '@fluentui/react-components';

const aiFadeSlideUp = {
  from: { opacity: 0, transform: 'translateY(18px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
};

const aiFadeIn = {
  from: { opacity: 0 },
  to: { opacity: 1 },
};

const aiShimmerBorder = {
  '0%': { backgroundPosition: '200% 0' },
  '100%': { backgroundPosition: '-200% 0' },
};

const aiOrbDrift = {
  from: { transform: 'translate(0px, 0px) scale(1)' },
  to: { transform: 'translate(32px, 24px) scale(1.08)' },
};

export const useStyles = makeStyles({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXXL,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalXXXL,
    boxSizing: 'border-box',
    '@media (max-width: 600px)': {
      gap: tokens.spacingVerticalXXL,
    },
  },

  animFadeSlideUp: {
    animationName: aiFadeSlideUp,
    animationDuration: '0.45s',
    animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    animationFillMode: 'both',
  },

  animFadeIn: {
    animationName: aiFadeIn,
    animationDuration: '0.4s',
    animationTimingFunction: 'ease-out',
    animationFillMode: 'both',
  },

  suiteSectionAnim: {
    animationName: aiFadeSlideUp,
    animationDuration: '0.5s',
    animationDelay: '0.25s',
    animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    animationFillMode: 'both',
  },

  pipelineSectionAnim: {
    animationName: aiFadeIn,
    animationDuration: '0.5s',
    animationDelay: '0.3s',
    animationFillMode: 'both',
  },

  // ─── Hero ────────────────────────────────────────────────────────────────────

  hero: {
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    paddingTop: '56px',
    paddingBottom: '56px',
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    animationName: aiFadeSlideUp,
    animationDuration: '0.55s',
    animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    animationFillMode: 'both',
    '@media (max-width: 768px)': {
      paddingTop: '36px',
      paddingBottom: '36px',
      paddingLeft: tokens.spacingHorizontalL,
      paddingRight: tokens.spacingHorizontalL,
    },
    '@media (max-width: 480px)': {
      paddingTop: '28px',
      paddingBottom: '28px',
      paddingLeft: tokens.spacingHorizontalM,
      paddingRight: tokens.spacingHorizontalM,
      borderRadius: tokens.borderRadiusLarge,
    },
  },

  heroTopBorder: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background:
      'linear-gradient(90deg, transparent 0%, #0078d4 30%, #7b5ea7 70%, transparent 100%)',
    backgroundSize: '200% 100%',
    animationName: aiShimmerBorder,
    animationDuration: '3s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
  },

  heroBottomBorder: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    background:
      'linear-gradient(90deg, transparent 0%, rgba(0,120,212,0.45) 30%, rgba(123,94,167,0.45) 70%, transparent 100%)',
  },

  heroDotGrid: {
    position: 'absolute',
    inset: 0,
    backgroundImage:
      'radial-gradient(circle, rgba(0,120,212,0.18) 1px, transparent 1px)',
    backgroundSize: '28px 28px',
    maskImage:
      'radial-gradient(ellipse 85% 85% at 50% 50%, black 40%, transparent 100%)',
    WebkitMaskImage:
      'radial-gradient(ellipse 85% 85% at 50% 50%, black 40%, transparent 100%)',
    pointerEvents: 'none',
  },

  heroOrbLeft: {
    position: 'absolute',
    top: '-80px',
    left: '-60px',
    width: '480px',
    height: '480px',
    borderRadius: '50%',
    background:
      'radial-gradient(circle, rgba(0,120,212,0.22) 0%, transparent 70%)',
    filter: 'blur(2px)',
    pointerEvents: 'none',
    animationName: aiOrbDrift,
    animationDuration: '9s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    animationDirection: 'alternate',
  },

  heroOrbRight: {
    position: 'absolute',
    bottom: '-100px',
    right: '-80px',
    width: '420px',
    height: '420px',
    borderRadius: '50%',
    background:
      'radial-gradient(circle, rgba(112,74,186,0.20) 0%, transparent 70%)',
    filter: 'blur(2px)',
    pointerEvents: 'none',
    animationName: aiOrbDrift,
    animationDuration: '11s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    animationDirection: 'alternate-reverse',
  },

  heroContent: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },

  heroBadgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    animationName: aiFadeSlideUp,
    animationDuration: '0.45s',
    animationDelay: '0.08s',
    animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    animationFillMode: 'both',
  },

  heroTitle: {
    maxWidth: '900px',
    animationName: aiFadeSlideUp,
    animationDuration: '0.5s',
    animationDelay: '0.14s',
    animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    animationFillMode: 'both',
  },

  heroTitleAccent: {
    color: tokens.colorBrandForeground1,
  },

  heroSubtitle: {
    maxWidth: '700px',
    color: tokens.colorNeutralForeground2,
    animationName: aiFadeSlideUp,
    animationDuration: '0.5s',
    animationDelay: '0.22s',
    animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    animationFillMode: 'both',
  },

  heroActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    paddingTop: tokens.spacingVerticalS,
    animationName: aiFadeSlideUp,
    animationDuration: '0.5s',
    animationDelay: '0.3s',
    animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    animationFillMode: 'both',
  },

  heroStats: {
    display: 'flex',
    gap: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    flexWrap: 'wrap',
    animationName: aiFadeSlideUp,
    animationDuration: '0.5s',
    animationDelay: '0.38s',
    animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    animationFillMode: 'both',
    '@media (max-width: 600px)': {
      gap: tokens.spacingHorizontalL,
    },
  },

  heroStat: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },

  heroStatValue: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
    lineHeight: 1,
  },

  heroStatLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },

  sectionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },

  sectionSub: {
    color: tokens.colorNeutralForeground2,
  },

  divider: {
    marginTop: `-${tokens.spacingVerticalL}`,
    marginBottom: `-${tokens.spacingVerticalL}`,
  },

  // ─── Pipeline section ─────────────────────────────────────────────────────────

  pipelineCard: {
    paddingTop: tokens.spacingVerticalXL,
    paddingBottom: tokens.spacingVerticalXL,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    overflowX: 'auto',
    '@media (max-width: 768px)': {
      paddingLeft: tokens.spacingHorizontalM,
      paddingRight: tokens.spacingHorizontalM,
    },
  },

  pipelineRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, minmax(110px, 1fr))',
    alignItems: 'center',
    minWidth: '750px',
  },

  pipelineItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: tokens.spacingVerticalXS,
    position: 'relative',
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },

  pipelineIconBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    flexShrink: 0,
  },

  pipelineTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
  },

  pipelineSub: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
  },

  pipelineConnector: {
    position: 'absolute',
    top: '16px',
    right: '-12px',
    color: tokens.colorNeutralForeground3,
    zIndex: 1,
  },

  // ─── Suite cards ─────────────────────────────────────────────────────────────

  suiteGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalM,
    '@media (max-width: 1100px)': {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    },
    '@media (max-width: 768px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
    },
  },

  suiteCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    minHeight: '200px',
    '@media (max-width: 600px)': {
      minHeight: 'auto',
    },
  },

  suiteIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    flexShrink: 0,
  },

  suiteTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },

  suiteDesc: {
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase300,
  },

  suiteActionLink: {
    cursor: 'pointer',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    marginTop: 'auto',
  },

  guideContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalM,
  },

  guideSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },

  guideSectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },

  guideList: {
    margin: 0,
    paddingLeft: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    color: tokens.colorNeutralForeground2,
  },
});
